import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { useQuery } from '@tanstack/react-query';
import { styled } from '@mui/material/styles';
import Image from 'next/image';
import { useParams } from 'next/navigation';
import { MediaPlayer, MediaProvider } from '@vidstack/react';
import { CustomTooltip } from '../styles';
import { BackdropComponent, AmountRaisedContainer } from './styles';
import { PlayButton } from './icons';
import { useSafeFeatureFlagVariantKey } from 'hooks/useSafeFeatureFlag';
import { getAmountRaised, getOffering, getOfferingHero } from 'services/offerings';
import { LEGACY_RESERVATION_LIST } from 'utils/constants/strings';
import { formatCurrencyToShorthand } from 'utils/helpers';
import { getAmountRaisedValue } from 'utils/offerings/helpers';
import useIsMobile from 'hooks/useIsMobile';
import useExcludeAmountOfferingList from 'hooks/useExcludeAmountOfferingList';
import useShowAmountRaisedList from 'hooks/useShowAmountRaisedList';
import useDayjs from 'hooks/useDayjs';
import useGetLiveStreams from 'hooks/useGetLiveStreams';
import LiveIndicator from 'components/CustomTombstone/LiveIndicator';

import '@vidstack/react/player/styles/base.css';

export const InvestInImage = styled('div')(({ theme, src }) => ({
  margin: '12px',
  backgroundImage: `url(${src})`,
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
  [theme.breakpoints.down('md')]: {
    display: 'flex',
    margin: '12px',
  },
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
    margin: '8px',
  },
}));

export const LogoContainer = styled('div')(({ theme }) => ({
  backgroundColor: 'white',
  boxShadow:
    '0px 3.6978390216827393px 4.329176902770996px 0px #00000007,0px 10.224053382873535px 11.969622611999512px 0px #0000000A,0px 24.61557960510254px 28.818239212036133px 0px #0000000D, 0px 81.6516342163086px 95.59217071533203px 0px #00000014',
  borderRadius: '36px',
  display: 'flex',
  [theme.breakpoints.up('sm')]: {
    borderRadius: '24px',
  },
}));

const logoContainerStyles = {
  mt: {
    xs: 2,
    sm: 0,
  },
  maxWidth: {
    xs: '100%',
    sm: '458px',
  },
  maxHeight: {
    xs: '100%',
    sm: '266px',
  },
  width: {
    xs: '312px',
    sm: '458px',
  },
  height: {
    xs: '182px',
    sm: '266px',
  },
  backgroundColor: 'white',
  padding: '12px',
  borderRadius: '24px',
  boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
};

const playButtonStyles = (isMobile) => ({
  background:
    'linear-gradient(126.6deg, rgba(241, 241, 241, 0.222) 28.69%, rgba(221, 221, 221, 0.12) 100%);',
  backdropFilter: 'blur(5px)',
  borderRadius: '543.888px',
  cursor: 'pointer',
  height: isMobile ? '64px' : '80px',
  top: 0,
  width: isMobile ? '64px' : '80px',
});

const amountRaisedContainerStyles = (isTTW, detached = false) => ({
  position: 'absolute',
  bottom: detached
    ? {
        xs: '4px',
        lg: '6px',
      }
    : {
        xs: '-20px',
        lg: '-35px',
      },
  left: {
    lg: 'auto',
  },
  width: {
    xs: isTTW ? 248 : 218,
    sm: isTTW ? 351 : 321,
  },
  height: {
    xs: 32,
    lg: 47,
  },
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: detached ? '16px' : '0px 0px 16px 16px',
  boxShadow: detached ? '0px 8px 18px rgba(0, 0, 0, 0.14)' : '-2px -2px 10px rgba(0, 0, 0, 0.1)',
});

const modalBoxStyles = (isMobile) => ({
  position: 'absolute',
  top: '45%',
  left: '45%',
  transform: 'translate(-40%, -40%)',
  maxWidth: '100%',
  width: isMobile ? '300px' : '900px',
  height: isMobile ? '200px' : '506px',
});

// Custom Video Player Component
const CustomVideoPlayer = ({ url }) => {
  const isYouTube = url.includes('youtube.com') || url.includes('youtu.be');
  const isVimeo = url.includes('vimeo.com');

  const getVideoId = () => {
    if (isYouTube) {
      const regex =
        /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com(?:\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|\/)|youtu\.be\/)([^&\n]{11})/;
      const match = url.match(regex);
      return match ? match[1] : null;
    }
    if (isVimeo) {
      const regex = /(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(\d+)/;
      const match = url.match(regex);
      return match ? match[1] : null;
    }
    return null;
  };

  const videoId = getVideoId();

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      {isYouTube && videoId && (
        <iframe
          width="100%"
          height="100%"
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
          frameBorder="0"
          allow="autoplay; encrypted-media"
          allowFullScreen
          title="YouTube Video"
        />
      )}
      {isVimeo && videoId && (
        <iframe
          width="100%"
          height="100%"
          src={`https://player.vimeo.com/video/${videoId}?autoplay=1`}
          frameBorder="0"
          allow="autoplay; fullscreen"
          allowFullScreen
          title="Vimeo Video"
        />
      )}
    </Box>
  );
};

export const HeroImage = ({ coverDesktopUrl }) => {
  const params = useParams();
  const mediaPlayerRef = useRef(null);
  const componentMounted = useRef(true);
  const [isHovering, setIsHovering] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [hasError, setHasError] = useState(false);
  const variant = useSafeFeatureFlagVariantKey('tombstone-video-autoplay');
  const isFeatureEnabled = variant === 'test';

  // Use consistent query key with CompanyComps.js to share cache
  const { data: offeringData, isLoading: isLoadingOffering } = useQuery({
    queryKey: ['offeringData'],
    queryFn: () => getOffering({ slug: params?.slug?.[0] }),
    staleTime: 15 * 60 * 1000, // 15 minutes - consistent with CompanyComps
    refetchOnWindowFocus: true, // Consistent refetch behavior
  });

  // Get live streams data to check if this offering is currently live
  const { data: liveStreamsData } = useGetLiveStreams();

  // Check if this offering is currently live streaming and get stream data
  const { isCurrentlyLive, activeStream } = useMemo(() => {
    const offeringSlug = params?.slug?.[0];
    if (!offeringSlug) return { isCurrentlyLive: false, activeStream: null };

    // Check against actual live stream data
    if (!liveStreamsData?.calls) return { isCurrentlyLive: false, activeStream: null };

    // Find the active live stream that matches this offering
    const matchingStream = liveStreamsData.calls.find((callData) => {
      const call = callData?.call;
      if (!call) return false;

      // Check different ways the offering might be identified in the live stream data
      return (
        call.custom?.slug === offeringSlug ||
        call.custom?.offeringSlug === offeringSlug ||
        call.custom?.title?.toLowerCase().includes(offeringData?.name?.toLowerCase()) ||
        call.slug === offeringSlug
      );
    });

    return {
      isCurrentlyLive: !!matchingStream,
      activeStream: matchingStream?.call || null,
    };
  }, [liveStreamsData, params?.slug, offeringData?.name]);

  const isApproved = offeringData && offeringData.status === 'approved';
  const isClosed = offeringData && offeringData.status === 'closed';

  const { data: offeringHeroSectionData } = useQuery({
    queryKey: ['offeringHeroSectionData', offeringData?.slug],
    queryFn: () => getOfferingHero({ slug: offeringData?.slug }),
    enabled: Boolean(offeringData?.slug) && isApproved && isClosed,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const heroImage = useMemo(() => {
    if (offeringHeroSectionData?.coverDesktopUrl && isApproved && isClosed) {
      return offeringHeroSectionData?.coverDesktopUrl;
    }

    if (coverDesktopUrl) {
      return coverDesktopUrl;
    }

    return offeringData?.coverDesktopUrl;
  }, [offeringHeroSectionData, offeringData, isApproved, isClosed, coverDesktopUrl]);

  const dayjs = useDayjs();

  const { data: EXCLUDE_AMOUNT_OFFERING_LIST, isSuccess: loadedExcluded } =
    useExcludeAmountOfferingList();
  const { data: SHOW_AMOUNT_RAISED_LIST, isSuccess: loadedShowAmount } = useShowAmountRaisedList();

  const isExcluded = useMemo(
    () => EXCLUDE_AMOUNT_OFFERING_LIST.includes(offeringData?.slug),
    [EXCLUDE_AMOUNT_OFFERING_LIST, offeringData],
  );

  const shouldShowAmount = useMemo(
    () =>
      loadedExcluded &&
      loadedShowAmount &&
      SHOW_AMOUNT_RAISED_LIST.includes(offeringData?.slug) &&
      !isExcluded,
    [loadedExcluded, loadedShowAmount, SHOW_AMOUNT_RAISED_LIST, offeringData, isExcluded],
  );

  const hideValue = useMemo(
    () => (loadedExcluded && loadedShowAmount ? isExcluded : true),
    [loadedExcluded, loadedShowAmount, isExcluded],
  );

  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);
  const isTTW = useMemo(() => offeringData && offeringData.status === 'testing', [offeringData]);
  const hasDisclosureHeavyHeroImage = offeringData?.slug === 'spark-fund-2';
  const rolloverDetails =
    Array.isArray(offeringData?.rolloverDetails) && offeringData?.rolloverDetails?.length
      ? offeringData?.rolloverDetails?.[0]
      : null;

  const getAmountRaisedTooltip = useCallback(() => {
    let tooltipText = '';
    if (
      offeringData?.slug === 'uplyft' ||
      offeringData?.slug === 'uplyft-inc' ||
      offeringData?.slug === 'regcf-cnote2-bmt'
    ) {
      tooltipText = (
        <>
          This is a combined raise amount from multiple offerings on substantially the same terms,
          shown in addition to the amount raised in this Regulation Crowdfunding offering.
          <br />
          <ul style={{ paddingLeft: '16px', paddingTop: '0', paddingBottom: '0' }}>
            <li>
              $752,242 raised from private investors in a Reg D offering from 2/21/2025 - 9/29/2025
            </li>
          </ul>
        </>
      );
    } else if (rolloverDetails) {
      tooltipText = (
        <>
          This is a combined raise amount from multiple offerings at the same terms in addition to
          the amount raised in this offering.
          <br />
          <ul style={{ paddingLeft: '16px' }}>
            <li>
              {formatCurrencyToShorthand(rolloverDetails.amountRaised)} raised under Reg CF through
              StartEngine Capital {dayjs(rolloverDetails.startDate ?? '').format('MMMM D, YYYY')} -
              {dayjs(rolloverDetails.endDate ?? '').format('MMMM D, YYYY')}
            </li>
          </ul>
        </>
      );
    }

    if (tooltipText !== '') {
      return <CustomTooltip content={tooltipText} contentTypographySx={{ textAlign: 'left' }} />;
    }
    return null;
  }, [rolloverDetails, dayjs, offeringData?.slug]);

  const { data: amountRaisedObject } = useQuery({
    queryKey: ['amountRaised', offeringData?.id],
    queryFn: () => getAmountRaised({ offeringId: offeringData?.id }),
    enabled:
      Boolean(offeringData?.id) &&
      !isLoadingOffering &&
      !LEGACY_RESERVATION_LIST.includes(offeringData?.slug),
    refetchOnWindowFocus: true, // Consistent with CompanyComps.js
  });

  const shouldShowAmountRaised = useMemo(() => {
    return (
      (!hideValue && Number.parseFloat(offeringData?.amountRaised) >= 124000) || shouldShowAmount
    );
  }, [hideValue, offeringData?.amountRaised, shouldShowAmount]);

  // Cleanup on component unmount
  useEffect(() => {
    // Store reference to current value of the ref
    const mediaPlayerRefCurrent = mediaPlayerRef.current;
    componentMounted.current = true;

    return () => {
      componentMounted.current = false;

      // Use the stored reference for cleanup
      if (mediaPlayerRefCurrent && mediaPlayerRefCurrent.dispose) {
        try {
          mediaPlayerRefCurrent.dispose();
        } catch {
          // Silently handle errors
        }
      }
    };
  }, []);

  const handleMouseEnter = useCallback(() => {
    setIsHovering(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovering(false);
  }, []);

  const toggleMute = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsMuted((prev) => !prev);
  }, []);

  const handleVideoError = useCallback(() => {
    setHasError(true);
  }, []);

  // Create a function to render the appropriate content instead of nested ternaries
  const renderContent = () => {
    // Show video when hovering, video is available, no errors, and feature is enabled
    if (isHovering && offeringData?.videoUrl && !hasError && isFeatureEnabled) {
      return (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            borderRadius: '12px',
            overflow: 'hidden',
          }}
        >
          <MediaPlayer
            ref={mediaPlayerRef}
            data-cy="hero-media-player"
            data-testid="media-player"
            style={{
              width: '100%',
              height: '100%',
            }}
            title={offeringData?.name}
            src={offeringData?.videoUrl}
            playsInline
            autoPlay
            muted={isMuted}
            preload="metadata"
            onPlay={() => {
              setIsMuted(true);
            }}
            onError={handleVideoError}
          >
            <MediaProvider data-cy="hero-media-provider" data-testid="media-provider" />
          </MediaPlayer>
          <IconButton
            onClick={toggleMute}
            style={{ position: 'absolute', top: 10, right: 10, color: 'white', zIndex: 1000 }}
            size="small"
            color="white"
            aria-label={isMuted ? 'Unmute video' : 'Mute video'}
            data-cy="hero-mute-toggle-button"
            data-testid="mute-toggle-button"
          >
            {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
          </IconButton>
        </Box>
      );
    }

    // Show image if available
    if (heroImage) {
      return (
        <Image
          src={heroImage}
          alt={`${offeringData?.name || 'Company'} cover image`}
          sizes="(max-width: 600px) 312px, 458px"
          style={{
            width: '100%',
            height: hasDisclosureHeavyHeroImage ? '100%' : 'auto',
            objectFit: hasDisclosureHeavyHeroImage ? 'contain' : undefined,
            objectPosition: hasDisclosureHeavyHeroImage ? 'center top' : undefined,
            borderRadius: '12px',
          }}
          width={458}
          height={266}
          priority
          fetchPriority="high"
          data-cy="next-image"
        />
      );
    }

    // Otherwise show skeleton loader
    return (
      <Skeleton width={isMobile ? 375 : 458} height={isMobile ? 'auto' : 266} variant="rounded" />
    );
  };

  // Show skeleton if still loading offering data
  if (isLoadingOffering) {
    return (
      <LogoContainer sx={logoContainerStyles}>
        <Skeleton width={isMobile ? 375 : 458} height={isMobile ? 'auto' : 266} variant="rounded" />
      </LogoContainer>
    );
  }

  return (
    <>
      <LogoContainer
        sx={{
          ...logoContainerStyles,
          position: 'relative',
          cursor: offeringData?.videoUrl ? 'pointer' : 'default',
          ...(hasDisclosureHeavyHeroImage ? { mb: { xs: '44px', sm: '64px' } } : null),
        }}
        onClick={offeringData?.videoUrl ? handleOpen : undefined}
        onMouseEnter={offeringData?.videoUrl ? handleMouseEnter : undefined}
        onMouseLeave={offeringData?.videoUrl ? handleMouseLeave : undefined}
        data-testid="logo-container"
        data-cy="logo-container"
      >
        {renderContent()}
        {/* Live stream indicator */}
        {isCurrentlyLive && (
          <LiveIndicator
            streamTitle="LIVE"
            streamName={activeStream?.custom?.title || offeringData?.name || 'StartEngine'}
            callId={activeStream?.id}
            data-cy="hero-live-indicator"
          />
        )}
      </LogoContainer>

      {offeringData?.videoUrl && (!isHovering || !isFeatureEnabled) && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 2,
            pointerEvents: 'auto',
          }}
          data-cy="play-button-container"
        >
          <PlayButton onClick={handleOpen} sx={playButtonStyles(isMobile)} data-cy="play-button" />
        </Box>
      )}

      {(shouldShowAmountRaised || rolloverDetails) && (
        <AmountRaisedContainer sx={amountRaisedContainerStyles(isTTW, hasDisclosureHeavyHeroImage)}>
          <Typography
            variant="h4b"
            color="primary"
            sx={{
              fontSize: {
                xs: '18px',
                sm: '24px',
                md: '28px',
              },
            }}
          >
            {getAmountRaisedValue({
              isTTW,
              amountRaisedObject,
              offeringData,
              showRaisedText: true,
            })}
          </Typography>
          {rolloverDetails && getAmountRaisedTooltip()}
        </AmountRaisedContainer>
      )}

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        BackdropComponent={BackdropComponent}
      >
        <Box sx={modalBoxStyles(isMobile)}>
          <CustomVideoPlayer url={offeringData?.videoUrl} onClose={handleClose} />
        </Box>
      </Modal>
    </>
  );
};

export default HeroImage;
